"use client"

import { useId } from "react"

/**
 * HeroMotion — animated home-page hero graphic.
 *
 * Tells Planami's core arc in a looping 24s CSS-only sequence:
 *   dream → write it down → plan (year·month·week·day) → act → see your wins → feel proud
 *
 * - Pure CSS/SVG, no JS runtime or timers → static-export safe.
 * - Reuses the app's design tokens (var(--primary), --card, --border, --chart-2 …),
 *   so it matches the brand and follows light/dark automatically.
 * - Honors prefers-reduced-motion by freezing on a readable "wins" frame.
 *
 * Fills its parent, so give the parent a size (fixed height or aspect ratio).
 */
export function HeroMotion() {
  const gradId = useId()

  return (
    <div className="phm" role="img" aria-label="Planami turns a dream into a plan you act on, so you can see your wins and feel proud.">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />

      <div className="phm-brand">
        <span className="phm-dot" />
        Planami <small>&nbsp;plan · reflect · grow</small>
      </div>

      {/* Scene 1: Dream */}
      <div className="phm-scene phm-s1">
        <div className="phm-dream">
          <div className="phm-glow" />
          <span className="phm-spark a" />
          <span className="phm-spark b" />
          <span className="phm-spark c" />
          <svg className="phm-star" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.2l2.5 5.9 6.4.5-4.9 4.2 1.5 6.2L12 15.8 6 19.2l1.5-6.2L2.6 8.6l6.4-.5z" />
          </svg>
        </div>
        <div className="phm-cap">Start with a <span className="phm-hi">dream</span></div>
        <div className="phm-sub">Picture the life you actually want</div>
      </div>

      {/* Scene 2: Write it down */}
      <div className="phm-scene phm-s2">
        <div className="phm-card">
          <div className="phm-goal"><span className="phm-gb" /><span className="phm-clip one">Run my first half-marathon</span></div>
          <div className="phm-goal"><span className="phm-gb" /><span className="phm-clip two">Build my own brand</span></div>
          <div className="phm-goal"><span className="phm-gb" /><span className="phm-clip three">Save for a trip to Japan</span></div>
          <svg width="220" height="16" viewBox="0 0 220 16">
            <path className="phm-penline" d="M4 11 C 40 3, 80 15, 120 8 S 200 4, 216 9" />
          </svg>
        </div>
        <div className="phm-cap">Write it <span className="phm-hi">down</span></div>
        <div className="phm-sub">Goals on paper feel real — and get done</div>
      </div>

      {/* Scene 3: Break it down */}
      <div className="phm-scene phm-s3">
        <div className="phm-flow">
          <div className="phm-panel p1">
            <h4>Year</h4>
            <div className="phm-grid g4">
              <span className="phm-cell" /><span className="phm-cell on" /><span className="phm-cell" /><span className="phm-cell" />
              <span className="phm-cell" /><span className="phm-cell" /><span className="phm-cell on2" /><span className="phm-cell" />
              <span className="phm-cell" /><span className="phm-cell on" /><span className="phm-cell" /><span className="phm-cell" />
            </div>
          </div>
          <svg className="phm-chev c1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
          <div className="phm-panel p2">
            <h4>Month</h4>
            <div className="phm-grid g7">
              <span className="phm-cell" /><span className="phm-cell" /><span className="phm-cell on" /><span className="phm-cell" /><span className="phm-cell" /><span className="phm-cell on2" /><span className="phm-cell" />
              <span className="phm-cell" /><span className="phm-cell on" /><span className="phm-cell" /><span className="phm-cell" /><span className="phm-cell" /><span className="phm-cell" /><span className="phm-cell on" />
              <span className="phm-cell" /><span className="phm-cell" /><span className="phm-cell" /><span className="phm-cell on2" /><span className="phm-cell" /><span className="phm-cell" /><span className="phm-cell" />
            </div>
          </div>
          <svg className="phm-chev c2" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
          <div className="phm-panel p3">
            <h4>Week</h4>
            <div className="phm-bars">
              <i style={{ height: "40%" }} /><i style={{ height: "70%" }} /><i className="p" style={{ height: "95%" }} /><i style={{ height: "55%" }} /><i style={{ height: "80%" }} /><i style={{ height: "35%" }} /><i style={{ height: "60%" }} />
            </div>
          </div>
          <svg className="phm-chev c3" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
          <div className="phm-panel p4">
            <h4>Day</h4>
            <div className="phm-list">
              <div className="phm-li"><span className="c" /><span className="ln" /></div>
              <div className="phm-li"><span className="c" /><span className="ln" /></div>
              <div className="phm-li"><span className="c" /><span className="ln" style={{ opacity: 0.5 }} /></div>
            </div>
          </div>
        </div>
        <div className="phm-cap">Break it into <span className="phm-hi">year · month · week · day</span></div>
        <div className="phm-sub">Big dreams become today&apos;s small step</div>
      </div>

      {/* Scene 4: Act */}
      <div className="phm-scene phm-s4">
        <div className="phm-todo">
          <div className="phm-task t1"><span className="phm-box"><svg viewBox="0 0 24 24"><path className="phm-tick" d="M5 12.5l4.5 4.5L19 7" /></svg></span>Morning run — 3km</div>
          <div className="phm-task t2"><span className="phm-box"><svg viewBox="0 0 24 24"><path className="phm-tick" d="M5 12.5l4.5 4.5L19 7" /></svg></span>Move $200 to savings</div>
          <div className="phm-task t3"><span className="phm-box"><svg viewBox="0 0 24 24"><path className="phm-tick" d="M5 12.5l4.5 4.5L19 7" /></svg></span>10 min of stretching</div>
          {/* <div className="phm-task t4"><span className="phm-box"><svg viewBox="0 0 24 24"><path className="phm-tick" d="M5 12.5l4.5 4.5L19 7" /></svg></span>Write one line of gratitude</div> */}
        </div>
        <div className="phm-cap"><span className="phm-hi">Act</span> — one small step at a time</div>
        <div className="phm-sub">No pressure. No burnout. Just today.</div>
      </div>

      {/* Scene 5: Notice your wins */}
      <div className="phm-scene phm-s5">
        <div className="phm-chart">
          <svg viewBox="0 0 400 180" preserveAspectRatio="none">
            <path className="phm-area" d="M10 150 L80 130 L150 138 L220 96 L290 74 L380 34 L380 175 L10 175 Z" />
            <path className="phm-spark-line" d="M10 150 L80 130 L150 138 L220 96 L290 74 L380 34" />
            <circle className="phm-node" cx="380" cy="34" r="6" />
          </svg>
          <div className="phm-badge">▲ +12 wins this week</div>
        </div>
        <div className="phm-cap">Notice your <span className="phm-hi">wins</span></div>
        <div className="phm-sub">Progress you can finally see</div>
      </div>

      {/* Scene 6: Be happy */}
      <div className="phm-scene phm-s6">
        <div className="phm-sun">
          <div className="phm-glow6" />
          <svg className="phm-sunsvg" viewBox="0 0 120 120">
            <defs>
              <radialGradient id={gradId} cx="42%" cy="36%" r="72%">
                <stop offset="0%" stopColor="var(--chart-2)" />
                <stop offset="100%" stopColor="var(--primary)" />
              </radialGradient>
            </defs>
            <g className="phm-rays">
              <line className="phm-ray" x1="100" y1="60" x2="112" y2="60" />
              <line className="phm-ray" x1="95" y1="80" x2="105" y2="86" />
              <line className="phm-ray" x1="80" y1="95" x2="86" y2="105" />
              <line className="phm-ray" x1="60" y1="100" x2="60" y2="112" />
              <line className="phm-ray" x1="40" y1="95" x2="34" y2="105" />
              <line className="phm-ray" x1="25" y1="80" x2="15" y2="86" />
              <line className="phm-ray" x1="20" y1="60" x2="8" y2="60" />
              <line className="phm-ray" x1="25" y1="40" x2="15" y2="34" />
              <line className="phm-ray" x1="40" y1="25" x2="34" y2="15" />
              <line className="phm-ray" x1="60" y1="20" x2="60" y2="8" />
              <line className="phm-ray" x1="80" y1="25" x2="86" y2="15" />
              <line className="phm-ray" x1="95" y1="40" x2="105" y2="34" />
            </g>
            <circle cx="60" cy="60" r="30" fill={`url(#${gradId})`} />
            <g className="phm-heart6" transform="translate(46.5,47) scale(1.12)">
              <path d="M12 21s-7.5-4.6-10-9.2C.6 8.8 2.2 5.5 5.4 5.1 7.4 4.9 9 6 12 8.7 15 6 16.6 4.9 18.6 5.1c3.2.4 4.8 3.7 3.4 6.7C19.5 16.4 12 21 12 21z" />
            </g>
          </svg>
        </div>
        <div className="phm-cap">Be <span className="phm-hi">happy</span> — whatever you do</div>
        <div className="phm-wordmark">Plan your life, track your progress, and actually feel proud of it — with <b>Planami</b></div>
      </div>

      {/* Narrative stepper */}
      <div className="phm-stepper">
        <span className="phm-step k1">Dream</span>
        <span className="phm-step k2">Write it down</span>
        <span className="phm-step k3">Plan it out</span>
        <span className="phm-step k4">Act</span>
        <span className="phm-step k5">See your wins</span>
        <span className="phm-step k6">Feel proud</span>
      </div>
    </div>
  )
}

const CSS = `
.phm{
  position:relative; width:100%; height:100%;
  border-radius:16px; overflow:hidden; container-type:inline-size;
  border:1px solid var(--border);
  color:var(--foreground);
  --phm-p-soft: color-mix(in oklab, var(--primary) 15%, transparent);
  --phm-gold: var(--chart-2);
  --phm-gold-soft: color-mix(in oklab, var(--chart-2) 22%, transparent);
  background:
    radial-gradient(140% 120% at 50% -20%, color-mix(in oklab, var(--card), transparent 20%), transparent 60%),
    var(--card);
  background-image:
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E");
  box-shadow: 0 20px 50px -28px color-mix(in oklab, var(--foreground) 40%, transparent);
}
.phm *{box-sizing:border-box}
.phm-brand{position:absolute;top:14px;left:16px;z-index:6;display:flex;align-items:center;gap:8px;font-weight:700;font-size:14px;color:var(--foreground)}
.phm-brand .phm-dot{width:10px;height:10px;border-radius:50%;background:var(--primary);box-shadow:0 0 0 4px var(--phm-p-soft)}
.phm-brand small{font-weight:500;color:var(--muted-foreground);font-size:11px}

.phm-scene{position:absolute;inset:0;z-index:2;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;text-align:center;padding:34px 26px 60px;opacity:0;will-change:opacity,transform}
.phm-cap{font-size:clamp(18px,3vw,26px);font-weight:700;letter-spacing:-.01em}
.phm-cap .phm-hi{color:var(--primary)}
.phm-sub{font-size:13.5px;color:var(--muted-foreground);margin-top:-6px}

.phm-s1{animation:phmS1 24s linear infinite}
.phm-s2{animation:phmS2 24s linear infinite}
.phm-s3{animation:phmS3 24s linear infinite}
.phm-s4{animation:phmS4 24s linear infinite}
.phm-s5{animation:phmS5 24s linear infinite}
.phm-s6{animation:phmS6 24s linear infinite}
@keyframes phmS1{0%{opacity:0;transform:translateY(14px) scale(.98)}2%{opacity:1;transform:none}14.5%{opacity:1;transform:none}16.7%{opacity:0;transform:translateY(-12px) scale(1.01)}100%{opacity:0}}
@keyframes phmS2{0%,16.7%{opacity:0;transform:translateY(14px) scale(.98)}19%{opacity:1;transform:none}31.2%{opacity:1;transform:none}33.3%{opacity:0;transform:translateY(-12px) scale(1.01)}100%{opacity:0}}
@keyframes phmS3{0%,33.3%{opacity:0;transform:translateY(14px) scale(.98)}35.6%{opacity:1;transform:none}47.8%{opacity:1;transform:none}50%{opacity:0;transform:translateY(-12px) scale(1.01)}100%{opacity:0}}
@keyframes phmS4{0%,50%{opacity:0;transform:translateY(14px) scale(.98)}52.3%{opacity:1;transform:none}64.5%{opacity:1;transform:none}66.7%{opacity:0;transform:translateY(-12px) scale(1.01)}100%{opacity:0}}
@keyframes phmS5{0%,66.7%{opacity:0;transform:translateY(14px) scale(.98)}69%{opacity:1;transform:none}81.2%{opacity:1;transform:none}83.3%{opacity:0;transform:translateY(-12px) scale(1.01)}100%{opacity:0}}
@keyframes phmS6{0%,83.3%{opacity:0;transform:translateY(14px) scale(.98)}85.6%{opacity:1;transform:none}97.5%{opacity:1;transform:none}100%{opacity:0}}

/* Scene 1: Dream */
.phm-dream{position:relative;width:150px;height:120px;display:grid;place-items:center}
.phm-star{width:76px;height:76px;color:var(--primary);filter:drop-shadow(0 8px 22px var(--phm-p-soft));animation:phmPulse 24s linear infinite}
@keyframes phmPulse{0%,16.7%{transform:scale(1)}4%{transform:scale(1.06)}8%{transform:scale(.98)}12%{transform:scale(1.04)}}
.phm-glow{position:absolute;width:150px;height:150px;border-radius:50%;background:radial-gradient(circle, var(--phm-gold-soft), transparent 62%);animation:phmBreathe 24s linear infinite}
@keyframes phmBreathe{0%,16.7%{opacity:.9;transform:scale(1)}8%{opacity:1;transform:scale(1.12)}}
.phm-spark{position:absolute;border-radius:50%;background:var(--phm-gold)}
.phm-spark.a{width:8px;height:8px;top:8px;left:20px;animation:phmFloat1 24s linear infinite}
.phm-spark.b{width:6px;height:6px;top:24px;right:16px;background:var(--primary);animation:phmFloat2 24s linear infinite}
.phm-spark.c{width:5px;height:5px;bottom:16px;left:34px;background:var(--chart-3);animation:phmFloat1 24s linear infinite .4s}
@keyframes phmFloat1{0%,16.7%{opacity:0;transform:translateY(6px)}3%{opacity:1}9%{transform:translateY(-10px)}15%{opacity:0}}
@keyframes phmFloat2{0%,16.7%{opacity:0;transform:translateY(-6px)}4%{opacity:1}10%{transform:translateY(10px)}15%{opacity:0}}

/* Scene 2: Write it down */
.phm-card{width:min(400px,88%);background:var(--background);border:1px solid var(--border);border-radius:14px;padding:18px 20px;box-shadow:0 12px 30px -20px color-mix(in oklab, var(--foreground) 40%, transparent);text-align:left}
.phm-goal{display:flex;align-items:center;gap:10px;margin:10px 0;color:var(--foreground);font-size:15px;font-weight:500}
.phm-goal .phm-gb{width:16px;height:16px;border-radius:5px;border:2px solid var(--primary);flex:none}
.phm-clip{overflow:hidden;white-space:nowrap}
.phm-clip.one{animation:phmType1 24s linear infinite}
.phm-clip.two{animation:phmType2 24s linear infinite}
.phm-clip.three{animation:phmType3 24s linear infinite}
@keyframes phmType1{0%,19%{max-width:0}22%{max-width:260px}100%{max-width:260px}}
@keyframes phmType2{0%,22%{max-width:0}25%{max-width:260px}100%{max-width:260px}}
@keyframes phmType3{0%,25%{max-width:0}28%{max-width:260px}100%{max-width:260px}}
.phm-penline{stroke:var(--primary);stroke-width:3;fill:none;stroke-linecap:round;stroke-dasharray:220;stroke-dashoffset:220;animation:phmDraw2 24s linear infinite}
@keyframes phmDraw2{0%,28%{stroke-dashoffset:220}31.5%{stroke-dashoffset:0}33.3%{stroke-dashoffset:0}100%{stroke-dashoffset:0}}

/* Scene 3: Break it down */
.phm-flow{display:flex;align-items:center;gap:8px;flex-wrap:nowrap;max-width:100%}
.phm-panel{background:var(--background);border:1px solid var(--border);border-radius:12px;padding:10px 11px;width:104px;box-shadow:0 8px 20px -16px color-mix(in oklab, var(--foreground) 40%, transparent);opacity:0}
.phm-panel h4{margin:0 0 7px;font-size:11px;color:var(--muted-foreground);font-weight:600;text-align:left}
.phm-grid{display:grid;gap:3px}
.phm-grid.g4{grid-template-columns:repeat(4,1fr)}
.phm-grid.g7{grid-template-columns:repeat(7,1fr)}
.phm-cell{aspect-ratio:1;border-radius:3px;background:var(--muted-foreground);opacity:.22}
.phm-cell.on{background:var(--primary);opacity:1}
.phm-cell.on2{background:var(--phm-gold);opacity:.9}
.phm-bars{display:flex;align-items:flex-end;gap:3px;height:44px}
.phm-bars i{flex:1;background:var(--chart-3);border-radius:2px;opacity:.6}
.phm-bars i.p{background:var(--primary);opacity:1}
.phm-list{display:flex;flex-direction:column;gap:6px}
.phm-li{display:flex;align-items:center;gap:6px;font-size:10px;color:var(--foreground)}
.phm-li .c{width:11px;height:11px;border-radius:3px;border:2px solid var(--primary);flex:none}
.phm-li .ln{height:5px;border-radius:3px;background:var(--muted-foreground);opacity:.28;flex:1}
.phm-chev{color:var(--primary);flex:none;opacity:0}
.phm-panel.p1{animation:phmPin1 24s linear infinite}
.phm-panel.p2{animation:phmPin2 24s linear infinite}
.phm-panel.p3{animation:phmPin3 24s linear infinite}
.phm-panel.p4{animation:phmPin4 24s linear infinite}
.phm-chev.c1{animation:phmCin1 24s linear infinite}
.phm-chev.c2{animation:phmCin2 24s linear infinite}
.phm-chev.c3{animation:phmCin3 24s linear infinite}
@keyframes phmPin1{0%,34%{opacity:0;transform:translateY(10px) scale(.92)}37%{opacity:1;transform:none}100%{opacity:1}}
@keyframes phmPin2{0%,37%{opacity:0;transform:translateY(10px) scale(.92)}40%{opacity:1;transform:none}100%{opacity:1}}
@keyframes phmPin3{0%,40%{opacity:0;transform:translateY(10px) scale(.92)}43%{opacity:1;transform:none}100%{opacity:1}}
@keyframes phmPin4{0%,43%{opacity:0;transform:translateY(10px) scale(.92)}46%{opacity:1;transform:none}100%{opacity:1}}
@keyframes phmCin1{0%,36.5%{opacity:0}38%{opacity:1}100%{opacity:1}}
@keyframes phmCin2{0%,39.5%{opacity:0}41%{opacity:1}100%{opacity:1}}
@keyframes phmCin3{0%,42.5%{opacity:0}44%{opacity:1}100%{opacity:1}}

/* Scene 4: Act */
.phm-todo{width:min(340px,86%);text-align:left;display:flex;flex-direction:column;gap:13px}
.phm-task{display:flex;align-items:center;gap:12px;font-size:16px;color:var(--foreground);font-weight:500}
.phm-box{width:24px;height:24px;border-radius:7px;border:2px solid var(--primary);flex:none;display:grid;place-items:center;background:var(--background)}
.phm-box svg{width:15px;height:15px}
.phm-tick{stroke:var(--primary);stroke-width:3.2;fill:none;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:26;stroke-dashoffset:26}
.phm-task.t1 .phm-tick{animation:phmTk1 24s linear infinite}
.phm-task.t2 .phm-tick{animation:phmTk2 24s linear infinite}
.phm-task.t3 .phm-tick{animation:phmTk3 24s linear infinite}
.phm-task.t4 .phm-tick{animation:phmTk4 24s linear infinite}
@keyframes phmTk1{0%,52.5%{stroke-dashoffset:26}54.5%{stroke-dashoffset:0}100%{stroke-dashoffset:0}}
@keyframes phmTk2{0%,55%{stroke-dashoffset:26}57%{stroke-dashoffset:0}100%{stroke-dashoffset:0}}
@keyframes phmTk3{0%,57.5%{stroke-dashoffset:26}59.5%{stroke-dashoffset:0}100%{stroke-dashoffset:0}}
@keyframes phmTk4{0%,60%{stroke-dashoffset:26}62%{stroke-dashoffset:0}100%{stroke-dashoffset:0}}
.phm-task.t1 .phm-box{animation:phmFill1 24s linear infinite}
.phm-task.t2 .phm-box{animation:phmFill2 24s linear infinite}
.phm-task.t3 .phm-box{animation:phmFill3 24s linear infinite}
.phm-task.t4 .phm-box{animation:phmFill4 24s linear infinite}
@keyframes phmFill1{0%,53%{background:var(--background)}55%{background:var(--phm-p-soft)}100%{background:var(--phm-p-soft)}}
@keyframes phmFill2{0%,55.5%{background:var(--background)}57.5%{background:var(--phm-p-soft)}100%{background:var(--phm-p-soft)}}
@keyframes phmFill3{0%,58%{background:var(--background)}60%{background:var(--phm-p-soft)}100%{background:var(--phm-p-soft)}}
@keyframes phmFill4{0%,60.5%{background:var(--background)}62.5%{background:var(--phm-p-soft)}100%{background:var(--phm-p-soft)}}

/* Scene 5: Notice your wins */
.phm-chart{width:min(400px,90%);height:170px;position:relative}
.phm-chart svg{width:100%;height:100%;overflow:visible}
.phm-spark-line{stroke:var(--primary);stroke-width:3.5;fill:none;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:520;stroke-dashoffset:520;animation:phmSl 24s linear infinite}
@keyframes phmSl{0%,68%{stroke-dashoffset:520}79%{stroke-dashoffset:0}83.3%{stroke-dashoffset:0}100%{stroke-dashoffset:520}}
.phm-area{fill:var(--phm-p-soft);opacity:0;animation:phmAr 24s linear infinite}
@keyframes phmAr{0%,72%{opacity:0}80%{opacity:1}83.3%{opacity:1}100%{opacity:0}}
.phm-node{fill:var(--background);stroke:var(--primary);stroke-width:3;opacity:0;animation:phmNd 24s linear infinite}
@keyframes phmNd{0%,78%{opacity:0}81%{opacity:1}83.3%{opacity:1}100%{opacity:0}}
.phm-badge{position:absolute;top:-6px;right:2px;background:var(--phm-gold);color:oklch(0.24 0.02 45);font-weight:700;font-size:15px;padding:7px 13px;border-radius:12px;box-shadow:0 10px 24px -12px var(--phm-gold);opacity:0;transform:scale(.6) rotate(-4deg);animation:phmBadge 24s linear infinite;display:flex;align-items:center;gap:6px;white-space:nowrap}
@keyframes phmBadge{0%,78%{opacity:0;transform:scale(.6) rotate(-4deg)}80.5%{opacity:1;transform:scale(1.12) rotate(2deg)}82%{transform:scale(1) rotate(0)}83.3%{opacity:1}100%{opacity:0}}

/* Scene 6: Be happy */
.phm-sun{position:relative;width:172px;height:172px;display:grid;place-items:center}
.phm-glow6{position:absolute;width:180px;height:180px;border-radius:50%;background:radial-gradient(circle, var(--phm-gold-soft), transparent 62%);animation:phmBreathe6 24s linear infinite}
@keyframes phmBreathe6{0%,83.3%{opacity:.85;transform:scale(1)}92%{opacity:1;transform:scale(1.08)}100%{opacity:.9;transform:scale(1.04)}}
.phm-sunsvg{position:relative;width:162px;height:162px;overflow:visible;filter:drop-shadow(0 12px 26px var(--phm-gold-soft))}
.phm-rays{transform-box:fill-box;transform-origin:center;animation:phmSpin 24s linear infinite}
@keyframes phmSpin{0%,83.3%{transform:rotate(0)}100%{transform:rotate(18deg)}}
.phm-ray{stroke:var(--phm-gold);stroke-width:5;stroke-linecap:round;opacity:.9}
.phm-heart6{fill:var(--card)}
.phm-wordmark{font-size:14.5px;color:var(--muted-foreground);max-width:440px}
.phm-wordmark b{color:var(--primary)}

/* Stepper */
.phm-stepper{position:absolute;left:0;right:0;bottom:14px;z-index:5;display:flex;align-items:center;justify-content:center;gap:6px;flex-wrap:wrap;padding:0 14px}
.phm-step{font-size:11px;color:var(--muted-foreground);padding:5px 10px;border-radius:999px;background:color-mix(in oklab, var(--card), transparent 30%);border:1px solid transparent}
.phm-step.k1{animation:phmSt1 24s linear infinite}
.phm-step.k2{animation:phmSt2 24s linear infinite}
.phm-step.k3{animation:phmSt3 24s linear infinite}
.phm-step.k4{animation:phmSt4 24s linear infinite}
.phm-step.k5{animation:phmSt5 24s linear infinite}
.phm-step.k6{animation:phmSt6 24s linear infinite}
@keyframes phmSt1{0%,16.7%{color:var(--primary);border-color:var(--phm-p-soft);background:var(--phm-p-soft)}16.8%,100%{color:var(--muted-foreground);border-color:transparent}}
@keyframes phmSt2{0%,16.7%{color:var(--muted-foreground)}17%,33.3%{color:var(--primary);border-color:var(--phm-p-soft);background:var(--phm-p-soft)}33.4%,100%{color:var(--muted-foreground);border-color:transparent}}
@keyframes phmSt3{0%,33.3%{color:var(--muted-foreground)}33.6%,50%{color:var(--primary);border-color:var(--phm-p-soft);background:var(--phm-p-soft)}50.1%,100%{color:var(--muted-foreground);border-color:transparent}}
@keyframes phmSt4{0%,50%{color:var(--muted-foreground)}50.3%,66.7%{color:var(--primary);border-color:var(--phm-p-soft);background:var(--phm-p-soft)}66.8%,100%{color:var(--muted-foreground);border-color:transparent}}
@keyframes phmSt5{0%,66.7%{color:var(--muted-foreground)}67%,83.3%{color:var(--primary);border-color:var(--phm-p-soft);background:var(--phm-p-soft)}83.4%,100%{color:var(--muted-foreground);border-color:transparent}}
@keyframes phmSt6{0%,83.3%{color:var(--muted-foreground)}83.6%,100%{color:var(--primary);border-color:var(--phm-p-soft);background:var(--phm-p-soft)}}

/* Responsive: shrink the year/month/week/day flow to the frame width */
@container (max-width: 560px){
  /* extra bottom padding so the (wrapping) stepper never overlaps the scene content */
  .phm-scene{padding:22px 16px 84px}
  .phm-panel{width:74px;padding:8px}
  .phm-flow{gap:5px}
  .phm-chev{width:13px;height:13px}
  .phm-bars{height:36px}
  .phm-card{padding:16px}
}
@container (max-width: 420px){
  .phm-panel{width:62px;padding:6px;border-radius:9px}
  .phm-panel h4{font-size:9px;margin-bottom:5px}
  .phm-flow{gap:3px}
  .phm-chev{width:11px;height:11px}
  .phm-cap{font-size:16px}
  .phm-sub{font-size:12px}
  .phm-task{font-size:14px}
  .phm-todo{gap:11px}
}

@media (prefers-reduced-motion: reduce){
  .phm-scene,.phm-star,.phm-glow,.phm-spark,.phm-penline,.phm-clip,.phm-panel,.phm-chev,.phm-tick,.phm-box,.phm-spark-line,.phm-area,.phm-node,.phm-badge,.phm-rays,.phm-glow6,.phm-step{animation:none !important}
  .phm-scene{opacity:0}
  .phm-s5{opacity:1}
  .phm-spark-line{stroke-dashoffset:0}
  .phm-area{opacity:1}
  .phm-node{opacity:1}
  .phm-badge{opacity:1;transform:none}
  .phm-step.k5{color:var(--primary);background:var(--phm-p-soft)}
}
`
